import {NestedTreeControl} from '@angular/cdk/tree';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatTreeNestedDataSource} from '@angular/material/tree';
import {ReasonCode, ReasonCodeService} from 'app/diary/reason-codes/reason-codes.service';

export interface ReasonCodeNode {
  id: string;
  name: string;
  displayName: string;
  children?: ReasonCodeNode[];
  parent: ReasonCodeNode;
}

@Component({
  selector: 'das-reason-codes',
  templateUrl: './reason-codes.component.html',
  styleUrls: ['./reason-codes.component.scss']
})
export class ReasonCodesComponent implements OnInit, OnChanges {
  constructor(private reasonCodeService: ReasonCodeService) {}
  private typeList: ReasonCodeNode[] = [];

  @Output() reasonCodeSender = new EventEmitter<string[]>();
  @Input() recommendedReasonCodes: ReasonCode[];

  private reasonCodes: ReasonCode[];
  public dataSource: MatTreeNestedDataSource<ReasonCodeNode>;
  public treeControl: NestedTreeControl<ReasonCodeNode>;

  public selectedReasonCodes: string[] = [];
  private allNodes: ReasonCodeNode[] = [];
  hasChild = (_: number, node: ReasonCodeNode) => !!node.children && node.children.length > 0;

  ngOnInit() {
    this.treeControl = new NestedTreeControl<ReasonCodeNode>(node => node.children);
    this.dataSource = new MatTreeNestedDataSource<ReasonCodeNode>();
    this.generateListOfCodes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recommendedReasonCodes'] && changes['recommendedReasonCodes'].currentValue) {
      this.loadRecommendedReasonCodes();
    }
  }

  private loadRecommendedReasonCodes(): void {
    if (this.recommendedReasonCodes) {
      this.recommendedReasonCodes.forEach((rc: ReasonCode) => {
        const node: ReasonCodeNode = this.getNodeForReasonCode(rc);
        if (node) {
          this.setSelectedReasonCodes({source: null, checked: true}, node);
          this.expandToNode(node);
        }
      });
    }
  }

  private getNodeForReasonCode(rc: ReasonCode): ReasonCodeNode {
    return this.allNodes.find(node => node.id === rc.id);
  }

  private expandToNode(node: ReasonCodeNode): void {
    let parentNode = node.parent;
    while (parentNode != null) {
      this.treeControl.expand(parentNode);
      parentNode = parentNode.parent;
    }
  }

  public isRecommended(node: ReasonCodeNode): boolean {
    return this.recommendedReasonCodes && this.selectedReasonCodes.includes(node.id);
  }

  public generateListOfCodes() {
    this.reasonCodeService.getAll().then(result => {
      this.reasonCodes = result;
      this.dataSource.data = this.buildReasonCodeTree(this.reasonCodes);
      this.loadRecommendedReasonCodes();
    });
  }

  public getId(reasonCode: ReasonCode): string {
    return reasonCode.id;
  }

  public getDescription(reasonCode: ReasonCode): string {
    return reasonCode.description;
  }

  public getType(reasonCode: ReasonCode): string {
    return reasonCode.type;
  }

  public getSubType(reasonCode: ReasonCode): string {
    return reasonCode.subType;
  }

  public getTypeName(reasonCode: ReasonCode): string {
    const stringSplitter: string[] = reasonCode.id.split('.');
    return stringSplitter[0] + '. ' + reasonCode.type;
  }

  public getSubTypeName(reasonCode: ReasonCode): string {
    const stringSplitter: string[] = reasonCode.id.split('.');
    return stringSplitter[0] + '.' + stringSplitter[1] + ' ' + reasonCode.subType;
  }

  public getCategoryName(reasonCode: ReasonCode): string {
    const stringSplitter: string[] = reasonCode.id.split('.');
    return stringSplitter[0] + '.' + stringSplitter[1] + '.' + stringSplitter[2] + ' ' + reasonCode.category;
  }

  public buildReasonCodeTree(reasonCodes: ReasonCode[]): ReasonCodeNode[] {
    this.typeList = [];
    reasonCodes.forEach((reasonCode: ReasonCode) => {
      let leafNode: ReasonCodeNode = null;
      const typeNode: ReasonCodeNode = this.getOrCreateNode(
        this.typeList,
        reasonCode,
        reasonCode.type,
        this.getTypeName,
        null
      );
      if (reasonCode.subType) {
        const subTypeNode: ReasonCodeNode = this.getOrCreateNode(
          typeNode.children,
          reasonCode,
          reasonCode.subType,
          this.getSubTypeName,
          typeNode
        );
        if (reasonCode.category) {
          const categoryNode: ReasonCodeNode = this.getOrCreateNode(
            subTypeNode.children,
            reasonCode,
            reasonCode.category,
            this.getCategoryName,
            subTypeNode
          );
          leafNode = this.createLeafNode(reasonCode, categoryNode);
          categoryNode.children.push(leafNode);
        } else {
          leafNode = this.createLeafNode(reasonCode, subTypeNode);
          subTypeNode.children.push(leafNode);
        }
      } else {
        leafNode = this.createLeafNode(reasonCode, typeNode);
        typeNode.children.push(leafNode);
      }
      this.allNodes.push(leafNode);
    });
    return this.typeList;
  }

  private createLeafNode(reasonCode: ReasonCode, parent: ReasonCodeNode): ReasonCodeNode {
    return {
      id: reasonCode.id,
      name: reasonCode.description,
      displayName: reasonCode.id + ' ' + reasonCode.description,
      children: null,
      parent: parent
    };
  }

  public setSelectedReasonCodes(event: MatCheckboxChange, checkedNode: ReasonCodeNode): void {
    if (event.checked) {
      this.selectedReasonCodes.push(checkedNode.id);
    } else {
      if (this.selectedReasonCodes.includes(checkedNode.id)) {
        this.selectedReasonCodes.splice(this.selectedReasonCodes.indexOf(checkedNode.id), 1);
      }
    }
    this.reasonCodeSender.emit(this.selectedReasonCodes.sort());
  }

  public getOrCreateNode(
    parentList: ReasonCodeNode[],
    reasonCode: ReasonCode,
    name: string,
    namingStrategy: (reasonCode: ReasonCode) => string,
    parent: ReasonCodeNode
  ): ReasonCodeNode {
    let theNode: ReasonCodeNode = parentList.find(sibling => sibling.name === name);

    if (!theNode) {
      parentList.push({
        id: reasonCode.id,
        name: name,
        displayName: namingStrategy(reasonCode),
        children: [],
        parent
      });
      theNode = parentList[parentList.length - 1];
    }

    return theNode;
  }
}
